import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useExpectedUploadedDocuments } from '@folio/utils/useExpectedUploadedDocuments';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import {
  DocumentCollectionDeadlineBanner,
  DocumentConformityChecklistModule,
  DocumentsUploadPolicyModule,
  DocumentsListsPolicyModule,
  OnDemandDocumentGenerationCard,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DeactivatableButton,
  useToast,
} from '@korint/folio';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { bumpDocumentsRegenSignal } from '@folio/utils/documentsRegenSignal';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  policyDocumentControllerGetPolicyDocumentConformityQueryOptions,
  keyValueFieldsToObject,
  legacyDocumentsControllerGenerateDocumentOnDemand,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerEditPolicy,
  policyControllerListPolicyDocumentsQueryOptions,
  usePolicyDocumentControllerAcceptAll,
  usePolicyControllerGeneratePolicySignatureRequest,
} from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';

export function PolicyDocuments() {
  const { t } = useTranslation();

  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    policy,
    workingBranch,
    hasOpenNewBusinessOrMtaBranch,
    mutate: refetchPolicy,
  } = usePolicyOnDashboard();
  const dashboardConfig = usePolicyProductConfig('brokerPolicyDashboardHome');

  const { data: policyholderDocuments } = useExpectedUploadedDocuments({
    productId: policy.productId,
    policyId: policy.id,
    branchId: workingBranch?.id,
  });

  const { data: nonDelegatedDocuments } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => data.nonDelegatedConfig?.documents?.documentTypes,
  });

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user?.brokerageFirmId;

  const { data: { firmType } = { firmType: undefined } } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
  });

  // Not local state: the backend recomputes and can overwrite this field asynchronously.
  const isDocumentValidated = (policy.customFields.allClientDocumentsOk as boolean) ?? false;

  const { mutateAsync: editPolicy, isPending } = useMutation({
    mutationFn: (validated: boolean) =>
      policyControllerEditPolicy(
        policy.id,
        { customFields: [{ key: 'allClientDocumentsOk', value: validated }] },
        { branchId: workingBranch?.id },
      ),
  });

  const {
    mutateAsync: generatePolicySignatureRequest,
    isPending: isGeneratePolicySignatureRequestPending,
  } = usePolicyControllerGeneratePolicySignatureRequest();

  const { data: policyDocuments, refetch: refetchPolicyDocuments } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    select: data =>
      data.data?.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const { mutateAsync: generateOnDemandDocument, isPending: isGeneratingDocument } = useMutation({
    mutationFn: (configName: string) =>
      legacyDocumentsControllerGenerateDocumentOnDemand({
        productId: policy.productId,
        configName,
        targetId: policy.id,
        ...(workingBranch?.id ? { branchId: workingBranch.id } : {}),
      }),
    onSuccess: () => {
      refetchPolicyDocuments();
      // The document lands in the list a beat after the response; this polls until it shows.
      bumpDocumentsRegenSignal(queryClient, policy.id);
      toast.toast({ title: t('documents.generateSuccess') });
    },
    onError: () => {
      toast.toast({ title: t('documents.generateError') });
    },
  });

  const { data: nonDelegatedDocumentsRequiredForSignature = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy?.productId!),
    enabled: Boolean(policy?.productId),
    select: ({ signature, nonDelegatedConfig }) =>
      nonDelegatedConfig?.documents?.documentTypes.filter(category =>
        signature.signedDocuments.find(
          ({ type, signature, neededForPolicySigned }) =>
            type === category && !neededForPolicySigned && signature === 'SIGN',
        ),
      ),
  });

  // Base branch's real id is the literal string "BASE" (see PolicyAggregateProjection).
  const conformityBranchId = workingBranch?.id ?? 'BASE';

  const documentConformityFlagEnabled = useFeatureFlag(
    'feat_document_conformity',
    policy.productId,
  );

  // DocumentsUploadPolicyModule/DocumentsListsPolicyModule fetch their own per-document
  // conformity internally; this query only drives the validate-switch below.
  const { isSuccess: isDocumentReviewEnabled, refetch: refetchConformity } = useQuery({
    ...policyDocumentControllerGetPolicyDocumentConformityQueryOptions(policy.id, {
      branchId: conformityBranchId,
    }),
    retry: false,
    enabled: documentConformityFlagEnabled,
  });

  const { mutate: acceptAllDocumentConformity } = usePolicyDocumentControllerAcceptAll({
    mutation: {
      onSuccess: () => {
        refetchPolicyDocuments();
        // Same query key as DocumentConformityChecklistModule's own fetch, so this also
        // refreshes its per-category status pills without waiting for a remount.
        refetchConformity();
        // Also refetches policy: the conformity consumer may have just recomputed allClientDocumentsOk.
        refetchPolicy();
      },
      onError: () => {
        toast.toast({
          title: t('documentsListsPolicy.conformity.acceptAllErrorTitle'),
          description: t('documentsListsPolicy.conformity.acceptAllError'),
          variant: 'destructive',
        });
      },
    },
  });

  const handleValidateSwitchChange = async (checked: boolean) => {
    // The switch also drives per-document conformity, so both stay in agreement.
    if (checked && isDocumentReviewEnabled) {
      // allClientDocumentsOk is recomputed asynchronously by the backend's conformity consumer
      // (RabbitMQ); refetch twice (immediate + delayed) instead of setting it optimistically.
      acceptAllDocumentConformity({
        policyId: policy.id,
        data: { branchId: conformityBranchId },
      });
      setTimeout(() => refetchPolicy(), 2000);
      return;
    }

    await editPolicy(checked);
    await refetchPolicy();
  };

  return (
    <Stack>
      {/* Merged into the checklist's header when there are required categories to show, so
          the switch and the checklist it drives read as one control. Kept visible even once
          documentReview drives allClientDocumentsOk automatically — a broker can still
          override it manually, and ~23 product funnels have relied on this switch existing
          for a long time; the module falls back to a standalone switch for those. */}
      <DocumentConformityChecklistModule
        validateSwitch={
          dashboardConfig.disableDocumentsValidation
            ? undefined
            : {
                checked: isDocumentValidated,
                onCheckedChange: handleValidateSwitchChange,
                disabled: isPending,
                label: t('documentsValidation.validateLabel'),
              }
        }
      />

      <DocumentCollectionDeadlineBanner policyId={policy.id} branchId={workingBranch?.id} />

      <DocumentsListsPolicyModule displayContext="broker" />

      <OnDemandDocumentGenerationCard
        onGenerate={configName => generateOnDemandDocument(configName)}
        isGenerating={isGeneratingDocument}
      />

      {Boolean(nonDelegatedDocuments?.length) && firmType === 'WHOLESALE' && (
        <DocumentsUploadPolicyModule
          displayContext="broker"
          documentsLists={
            nonDelegatedDocuments?.map(category => ({ category, documentType: category })) ?? []
          }
          categoryType="nonDelegatedDocuments"
          useBranchId={Boolean(workingBranch?.id)}
          extraActions={
            <Group gap={2}>
              <DeactivatableButton
                onClick={async () => {
                  await generatePolicySignatureRequest({
                    policyId: policy.id,
                    data: { deliveryMode: 'EMAIL', neededForPolicySigned: false },
                    params: { branchId: workingBranch?.id },
                  });

                  toast.toast({
                    title: 'Demande de signature envoyée',
                    description: 'Les documents ont été envoyés en signature avec succès.',
                  });
                }}
                loading={isGeneratePolicySignatureRequestPending}
                rules={[
                  {
                    isValid: Boolean(workingBranch?.id) || !hasOpenNewBusinessOrMtaBranch,
                    message:
                      "Pour uploader les documents liés à un avenant, rendez-vous sur l'avenant concerné via le bouton « Explorer les avenants ».",
                  },
                  ...nonDelegatedDocumentsRequiredForSignature.map(documentType => {
                    const documentsOfType = (policyDocuments ?? []).filter(
                      ({ metadata, type, category }) =>
                        type === 'non_delegated' &&
                        category === documentType &&
                        metadata.archived === 'false',
                    );

                    const isMissingSignature = documentsOfType.some(
                      doc => doc.metadata.signed === undefined || doc.metadata.signed === 'false',
                    );

                    return {
                      isValid: documentsOfType.length > 0 && isMissingSignature,
                      message: `En attente du téléversement du document "${t(
                        `documents.${documentType}`,
                      )}".`,
                    };
                  }),
                ]}
              >
                Envoyer en signature
              </DeactivatableButton>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="ml-2 h-4 w-4 text-muted-foreground cursor-help font-normal" />
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm">
                    La signature de ces documents n'est pas obligatoire pour la finalisation du
                    devis.
                  </div>
                </TooltipContent>
              </Tooltip>
            </Group>
          }
          authorizedFormats={{
            'application/pdf': ['.pdf'],
          }}
        />
      )}

      {Boolean(policyholderDocuments?.length) && (
        <DocumentsUploadPolicyModule
          displayContext="broker"
          documentsLists={policyholderDocuments ?? []}
          useBranchId={Boolean(workingBranch?.id)}
          // Switch above already triggers accept-all; hiding avoids two controls doing one thing.
          hideAcceptAllButton={!dashboardConfig.disableDocumentsValidation}
        />
      )}
    </Stack>
  );
}
